import React from 'react';
import { iconPaths, iconFitViewBox } from './iconPaths';

const DEFAULT_ICON = 'FaBoxes';

/**
 * Stroke tabanlı ikonlar (fill yerine çizgi). Bu motor varsayılan olarak path'i fill ile
 * çizdiği için, Core'daki Feather "activity" gibi çizgi ikonları burada özel render edilir
 * — böylece menü ikonu tüm ürünlerde birebir aynı görünür.
 */
const STROKE_ICONS: Record<string, string[]> = {
  activity: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  FaLedger: ['M5 3h15v18H5z', 'M9 3v18', 'M12 8h5', 'M12 12h5', 'M12 16h4'],
  FaAsset: ['M3 21h18', 'M5 21V7l7-4 7 4v14', 'M9 9h2v2H9z', 'M14 9h2v2h-2z', 'M9 15h2v2H9z', 'M14 15h2v2h-2z'],
  FaImport: ['M5 3h9l5 5v13H5v-4', 'M14 3v5h5', 'M2 13h10', 'M8 9l4 4-4 4'],
  FaExport: ['M5 3h9l5 5v13H5v-4', 'M14 3v5h5', 'M2 13h10', 'M10 9l-4 4 4 4'],
  FaApproval: ['m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z', 'm8 12 3 3 5-6'],
  FaConsent: ['M4 4h16v16H4z', 'M8 9h8', 'M8 13h8', 'M8 17h5', 'm15 17 2 2 4-5'],
  checkCircle: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'm8 12 3 3 5-6'],
  tag: ['M11 3H4v7l9 9 7-7-9-10Z', 'M7 7h.01'],
  // Bildirimler — app switcher Notification ile aynı zil (Feather outline)
  bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
};

const ICON_ALIASES: Record<string, string> = {
  dashboard: 'FaThLarge',
  settings: 'FaCog',
  folder: 'FaBoxes',
  companies: 'FaBuilding',
  business: 'FaBuilding',
  organization: 'FaSitemap',
  roles: 'FaBuilding',
  users: 'FaUsers',
  person: 'FaUsers',
  email: 'FaGlobe',
  database: 'FaBoxes',
  robot: 'FaGamepad',
  schedule: 'FaCalendarCheck',
  scheduler: 'FaCalendarCheck',
  language: 'FaGlobe',
  lock: 'FaBuilding',
  chevronLeft: 'FaThLarge',
  chevronRight: 'FaThLarge',
  expandMore: 'FaThLarge',
};

interface IconProps {
  name: string;
  size?: number | string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Uygulama / menü ikonları — iconPaths ile render.
 * iconFitViewBox'ta tanımlı ikonlar path'i ortalanıp ölçeklenir (küçük/sola yaslı görünümü önler).
 */
export function Icon({ name, size = 18, color, className = '', style }: IconProps) {
  const strokePaths = STROKE_ICONS[name];
  if (strokePaths) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ color: color || 'currentColor', ...style }}
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {strokePaths.map((d, i) => <path key={i} d={d} />)}
      </svg>
    );
  }
  const resolvedName = iconPaths[name] ? name : (ICON_ALIASES[name] ?? DEFAULT_ICON);
  const path = iconPaths[resolvedName] ?? iconPaths[DEFAULT_ICON];
  if (!path) return null;
  const fit = iconFitViewBox[resolvedName];
  const pathContent = fit ? (
    <g transform={`translate(12,12) scale(${fit[2]}) translate(${-fit[0]},${-fit[1]})`}>
      <path d={path} />
    </g>
  ) : (
    <path d={path} />
  );
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ color: color || 'currentColor', ...style }}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {pathContent}
    </svg>
  );
}
